import { useMemo, useState } from "react";
import { CATS, SCENES, type CatKey } from "@/mocks/gossip";

type Filter = "全部" | CatKey;
const ALL: Filter[] = ["全部", ...CATS.map((c) => c.key)];

export default function SceneArchive() {
  const [active, setActive] = useState<Filter>("全部");

  const list = useMemo(() => {
    if (active === "全部") return SCENES;
    return SCENES.filter((s) => s.cat === active);
  }, [active]);

  return (
    <section id="archive" className="bg-background-50 py-16 md:py-24">
      <div className="mx-auto max-w-7xl px-4 md:px-6">
        <div className="flex flex-col gap-2 md:items-end md:flex-row md:justify-between">
          <div>
            <span className="inline-flex items-center gap-1.5 text-xs font-semibold tracking-widest text-accent-700 uppercase">
              <i className="ri-scissors-2-line" aria-hidden="true"></i>
              Archive
            </span>
            <h2 className="mt-2 font-heading text-3xl font-bold text-foreground-950 md:text-4xl">名场面图鉴</h2>
            <p className="mt-3 max-w-xl text-sm leading-relaxed text-foreground-700">
              明星、网红、综艺与剧组的高能瞬间，按圈层归档整理。点击栏目标签即可快速筛选，吃透每一层现场。
            </p>
          </div>
          <span className="inline-flex w-fit items-center gap-2 rounded-full bg-secondary-100 px-4 py-1.5 text-xs font-medium text-secondary-900 whitespace-nowrap">
            <i className="ri-time-line" aria-hidden="true"></i>
            本周收录 {SCENES.length} 条
          </span>
        </div>

        <div className="mt-8 flex flex-wrap gap-2 rounded-full bg-background-100 p-1.5 w-full sm:w-fit">
          {ALL.map((f) => (
            <button
              key={f}
              type="button"
              onClick={() => setActive(f)}
              className={`rounded-full px-5 py-2 text-sm font-medium whitespace-nowrap transition-colors ${
                active === f
                  ? "bg-primary-500 text-background-50 shadow-none"
                  : "text-foreground-700 hover:bg-background-200/70"
              }`}
            >
              {f}
            </button>
          ))}
        </div>

        <div className="mt-8 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {list.map((s) => (
            <article
              key={s.id}
              className="group flex flex-col overflow-hidden rounded-2xl border border-background-200 bg-background-50 transition-all hover:border-primary-200"
            >
              <a href="#top" onClick={(e) => e.preventDefault()} className="block overflow-hidden" aria-label={s.title}>
                <img
                  src={s.img}
                  alt={s.title}
                  className="aspect-[4/3] w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                />
              </a>
              <div className="flex flex-1 flex-col p-5">
                <div className="flex items-center gap-2">
                  <span className="rounded-full bg-accent-100 px-2.5 py-1 text-xs font-semibold text-accent-800 whitespace-nowrap">
                    {s.cat}
                  </span>
                  <span className="text-xs text-foreground-500 whitespace-nowrap">{s.tag}</span>
                </div>
                <h3 className="mt-3 font-heading text-lg font-bold leading-snug text-foreground-950">
                  <a href="#top" onClick={(e) => e.preventDefault()} className="transition-colors hover:text-primary-600">
                    {s.title}
                  </a>
                </h3>
                <p className="mt-2 flex-1 text-sm leading-relaxed text-foreground-700">{s.excerpt}</p>
                <div className="mt-4 flex items-center justify-between border-t border-background-200 pt-3 text-xs text-foreground-500">
                  <span className="flex items-center gap-1.5 whitespace-nowrap">
                    <i className="ri-time-line" aria-hidden="true"></i>
                    {s.read}
                  </span>
                  <span className="whitespace-nowrap">{s.time}</span>
                  <span className="text-foreground-400 transition-transform group-hover:translate-x-0.5">
                    <i className="ri-arrow-right-line" aria-hidden="true"></i>
                  </span>
                </div>
              </div>
            </article>
          ))}
        </div>

        <p className="mt-10 text-center text-sm text-foreground-500">
          共收录 <span className="font-semibold text-primary-600">{list.length}</span> 条「{active}」相关名场面，更多内容持续整理中
        </p>
      </div>
    </section>
  );
}